import { useMemo } from 'react'

import useWindowSize from '@/hooks/useWindowSize'
import { useSessionStore } from '@/stores/useSessionStore'
import { useWidgetStore } from '@/stores/useWidgetStore'
import type { WelcomeMessageDesign, WidgetIconDesign, WidgetBreakpointTab } from '@/types'
import { getBreakpointSettings, getActualBreakpoint } from '@/utils/widget-utils'
import { useWidgetBreakpoint } from './useWidgetBreakpoint'

/**
 * Hook to get the current chat widget breakpoint icon settings based on screen width.
 *
 * Uses debouncing (100ms by default) to prevent excessive re-renders on window resize.
 * The breakpoint is recalculated only when the screen width changes.
 *
 * In demo mode, breakpoint overrides can be set via postMessage to force specific
 * breakpoints for icon and welcome message independently.
 *
 * @param delay - Debounce delay in milliseconds (default: 100ms)
 * @returns The current WidgetIconDesign and WelcomeMessageDesign
 */
export function useWidgetBreakpointSettings(delay = 100): {
  iconSettings: WidgetIconDesign | undefined
  welcomeMessageSettings: WelcomeMessageDesign | undefined
} {
  const viewportBreakpoint = useWidgetBreakpoint(delay)
  const { screenSize } = useWindowSize(delay)
  const demo = useWidgetStore((state) => state.demo)
  const breakpointOverrides = useWidgetStore((state) => state.breakpointOverrides)

  const widgetSettings = useSessionStore((state) => state.widgetSettings)
  const iconDesignByBreakpoint = widgetSettings?.iconSettings?.designByBreakpoint ?? {}
  const welcomeMessageSettingsByBreakpoint = widgetSettings?.welcomeMessageSettings?.designByBreakpoint ?? {}

  const actualScreenBreakpoint = useMemo(() => getActualBreakpoint(screenSize.width), [screenSize.width])

  const iconBreakpoint = useMemo<WidgetBreakpointTab>(() => {
    if (demo && breakpointOverrides.icon) {
      return breakpointOverrides.icon
    }
    return actualScreenBreakpoint
  }, [demo, breakpointOverrides.icon, actualScreenBreakpoint])

  const welcomeMessageBreakpoint = useMemo<WidgetBreakpointTab>(() => {
    if (demo && breakpointOverrides.welcomeMessage) {
      return breakpointOverrides.welcomeMessage
    }
    return viewportBreakpoint
  }, [demo, breakpointOverrides.welcomeMessage, viewportBreakpoint])

  const iconSettings = useMemo(() => {
    const breakpointDesign = getBreakpointSettings<WidgetIconDesign>(
      iconBreakpoint,
      iconDesignByBreakpoint
    )
    const parentSettings = widgetSettings?.iconSettings

    if (!breakpointDesign && !parentSettings) {
      return undefined
    }

    return {
      displaySettings: parentSettings?.displaySettings,
      ...breakpointDesign,
    } as WidgetIconDesign
  }, [iconBreakpoint, iconDesignByBreakpoint, widgetSettings?.iconSettings])

  const welcomeMessageSettings = useMemo(() => {
    const breakpointDesign = getBreakpointSettings<WelcomeMessageDesign>(
      welcomeMessageBreakpoint,
      welcomeMessageSettingsByBreakpoint
    )
    const parentSettings = widgetSettings?.welcomeMessageSettings

    if (!breakpointDesign && !parentSettings) {
      return undefined
    }

    return {
      displaySettings: parentSettings?.displaySettings,
      ...breakpointDesign,
    } as WelcomeMessageDesign
  }, [welcomeMessageBreakpoint, welcomeMessageSettingsByBreakpoint, widgetSettings?.welcomeMessageSettings])

  return { iconSettings, welcomeMessageSettings }
}
